'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/lib/stores/auth';

interface FollowButtonProps {
  userId: string;
  size?: 'sm' | 'md';
  /** 关注/取关成功后回调（父组件可重拉 target profile 刷新粉丝数）。 */
  onFollowChange?: () => void;
}

export function FollowButton({ userId, size = 'md', onFollowChange }: FollowButtonProps) {
  const { t } = useTranslation('common');
  const { user, token, refreshUser } = useAuthStore();
  const [isFollowing, setIsFollowing] = useState(false);
  // target 是否也关注了当前用户（check 返回，关注/取关后保持不变）
  const [isFollowedBy, setIsFollowedBy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/follows/${userId}/check`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((r: { isFollowing: boolean; isFollowedBy?: boolean }) => {
        setIsFollowing(r.isFollowing);
        setIsFollowedBy(!!r.isFollowedBy);
      })
      .catch(() => {});
  }, [token, userId]);

  // Don't show for own profile
  if (user && user.id === userId) return null;

  async function toggle() {
    if (!token) {
      const locale = window.location.pathname.split('/')[1];
      window.location.href = `/${locale}/login`;
      return;
    }
    setLoading(true);
    try {
      if (isFollowing) {
        await fetch(`/api/follows/${userId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        setIsFollowing(false);
      } else {
        await fetch(`/api/follows/${userId}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        setIsFollowing(true);
        // isFollowedBy 保持：target 是否关注 viewer 不因 viewer 关注而变，
        // 互关（isFollowing && isFollowedBy）由本地派生，自动正确。
      }
      // viewer 自己的关注计数（me.following）变了 → 刷新让 self 卡 + 右上角名片同步。
      void refreshUser();
      // target 的粉丝数变了 → 通知父组件重拉 target profile。
      onFollowChange?.();
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  // 互关 = 双向关注
  const isMutual = isFollowing && isFollowedBy;

  const label = loading
    ? '...'
    : isFollowing
      ? hovered
        ? t('unfollow')
        : isMutual
          ? t('mutualFollowing')
          : t('following')
      : t('follow');

  const sizeClass =
    size === 'sm'
      ? 'px-3 py-1 text-xs'
      : 'px-4 py-1.5 text-sm';

  return (
    <button
      onClick={toggle}
      disabled={loading}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      className={`${sizeClass} rounded-lg font-medium transition-colors disabled:opacity-50 ${
        isFollowing
          ? 'border border-border text-muted-foreground hover:border-error/30 hover:text-error hover:bg-error-bg'
          : 'bg-brand-600 text-white hover:bg-brand-700'
      }`}
    >
      {label}
    </button>
  );
}
